import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  List,
  ListItem,
  Link,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { HomeHeader } from '../components/ui';
import { HomeFooter } from '../components/ui';

const BulletList = ({ items }) => (
  <List dense sx={{ listStyleType: 'disc', pl: 4, mb: 2 }}>
    {items.map((item, index) => (
      <ListItem
        key={index}
        sx={{
          display: 'list-item',
          py: 0.25,
          pl: 0,
        }}
      >
        <Typography variant="body1">{item}</Typography>
      </ListItem>
    ))}
  </List>
);

const NumberedList = ({ items }) => (
  <List component="ol" dense sx={{ listStyleType: 'decimal', pl: 4, mb: 2 }}>
    {items.map((item, index) => (
      <ListItem
        key={index}
        sx={{
          display: 'list-item',
          py: 0.25,
          pl: 0,
        }}
      >
        <Typography variant="body1">{item}</Typography>
      </ListItem>
    ))}
  </List>
);

const Section = ({ title, children }) => (
  <Box component="section" sx={{ mt: 4 }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const SubprocessorEntry = ({ name, purpose, categories, location, transfer }) => (
  <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
      {name}
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Purpose:</strong> {purpose}
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Categories of Personal Data:</strong> {categories}
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Processing Location:</strong> {location}
    </Typography>
    <Typography variant="body2">
      <strong>Transfer Mechanism:</strong> {transfer}
    </Typography>
  </Box>
);

const DPA = () => {
  const navigate = useNavigate();

  return (
    <>
      <HomeHeader />

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button variant="text" sx={{ mb: 2 }} onClick={() => navigate('/')}>
          Back to Home
        </Button>

        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          Data Processing Agreement
        </Typography>

        <Typography variant="h6" gutterBottom>
          Itclarifies Learning Platform
        </Typography>

        <Typography variant="subtitle2" gutterBottom>
          Version: 1.0
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          Effective Date: June 15, 2026
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          Published at:{' '}
          <Link href="https://www.itclarifies.com/dpa" target="_blank" rel="noopener noreferrer">
            https://www.itclarifies.com/dpa
          </Link>
        </Typography>

        <Typography variant="body1" paragraph sx={{ mt: 3 }}>
          This Data Processing Agreement, including its annexes, forms part of the Agreement
          between Itclarifies and Customer governing Customer&apos;s use of the Itclarifies learning
          platform.
        </Typography>

        <Typography variant="body1" paragraph>
          This DPA applies automatically when Customer subscribes to, accesses, or uses the Platform
          and Itclarifies Processes Personal Data on behalf of Customer.
        </Typography>

        <Typography variant="body1" paragraph>
          No separate signature of this DPA is required unless Itclarifies expressly requires one.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Section title="1. Parties">
          <Typography variant="body1" paragraph>
            This DPA is entered into between:
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            Customer
          </Typography>
          <Typography variant="body1" paragraph>
            The organization, business, association, public-sector entity, or other legal person
            identified in the applicable Order Form, subscription confirmation, invoice, subscription
            order, quote, tenant activation confirmation, online registration, or other agreement
            or record governing Customer&apos;s use of the Platform.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            Itclarifies
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies AB
            <br />
            Company registration number: 556967-9623
            <br />
            Address: Margretelund 1, 184 60 Åkersberga, Sweden
            <br />
            Email:{' '}
            <Link href="mailto:info@itclarifies.com">info@itclarifies.com</Link>
          </Typography>

          <Typography variant="body1" paragraph>
            Customer and Itclarifies are each referred to as a Party and together as the Parties.
          </Typography>
        </Section>

        <Section title="2. Background">
          <Typography variant="body1" paragraph>
            Customer uses the Itclarifies learning platform for internal learning, training,
            game-based knowledge activities, examinations, learning analytics, and export of
            examination results.
          </Typography>
          <Typography variant="body1" paragraph>
            In connection with providing the platform, Itclarifies may process Personal Data on
            behalf of Customer.
          </Typography>
          <Typography variant="body1" paragraph>
            The Parties agree that this DPA governs Itclarifies&apos; processing of Personal Data as
            processor on behalf of Customer.
          </Typography>
        </Section>

        <Section title="3. Definitions">
          <Typography variant="body1" paragraph>
            In this DPA:
          </Typography>
          <BulletList
            items={[
              'Agreement means the Terms and Conditions, applicable Order Form, subscription confirmation, invoice, subscription agreement, tenant activation confirmation, online registration, or other written or electronic agreement or record governing Customer\u2019s use of the Platform.',
              'Applicable Data Protection Law means the GDPR, applicable Swedish data-protection legislation, and any other data-protection or privacy laws applicable to the processing of Personal Data under this DPA.',
              'Controller has the meaning given in the GDPR.',
              'Customer Content has the meaning given in the Agreement.',
              'Customer Data has the meaning given in the Agreement. Customer Data is used in this DPA as a data-protection, confidentiality, security, export, deletion, and service-operation concept and does not alter the ownership and intellectual-property rights set out in the Agreement.',
              'Customer-Specific Generated Content has the meaning given in the Agreement.',
              'Data Subject has the meaning given in the GDPR.',
              'Direct Account Identifiers means Personal Data that directly identifies or enables direct contact with a user, including name, username, email address, external authentication identifiers, and similar account-level identifiers.',
              'De-identified User Record means a user record where Direct Account Identifiers have been removed, overwritten, or otherwise made unavailable in the active production environment, while internal technical identifiers may be retained to preserve referential integrity, auditability, session history, assessment history, security records, or contractual records.',
              'DPA means this Data Processing Agreement and all annexes.',
              'GDPR means Regulation (EU) 2016/679, the General Data Protection Regulation.',
              'Historical Learning Records means historical game play session records generated through use of the Platform, including session participation, answers, scores, progress data, completion status, assessment results, generated feedback, exports, audit records, and related metadata.',
              'Personal Data means any personal data, as defined in the GDPR, that is Processed by Itclarifies on behalf of Customer under the Agreement.',
              'Personal Data Breach has the meaning given in the GDPR.',
              'Platform has the meaning given in the Agreement.',
              'Platform IP has the meaning given in the Agreement.',
              'Process, Processed, and Processing have the meanings given in the GDPR.',
              'Processor has the meaning given in the GDPR.',
              'Pseudonymised Data means Personal Data that can no longer be attributed to a specific Data Subject without the use of additional information, provided that such additional information is kept separately or is otherwise protected by appropriate technical and organizational measures.',
              'Subprocessor means any third party engaged by Itclarifies to Process Personal Data on behalf of Customer.',
              'Supervisory Authority means a competent data-protection authority, including the Swedish Authority for Privacy Protection where applicable.',
            ]}
          />
          <Typography variant="body1" paragraph>
            Terms not defined in this DPA have the meaning given in the Agreement or the GDPR.
          </Typography>
        </Section>

        <Section title="4. Relationship between the Parties">
          <Typography variant="body1" paragraph>
            For Personal Data processed inside Customer&apos;s database tenant, organization account,
            learning environment, games, sessions, uploaded materials, exports, or user management
            functions:
          </Typography>
          <NumberedList
            items={[
              'Customer is the Controller.',
              'Itclarifies is the Processor.',
              'Itclarifies Processes Personal Data only on behalf of Customer and only in accordance with this DPA and Customer\u2019s documented instructions.',
            ]}
          />
          <Typography variant="body1" paragraph>
            Customer determines the purposes and means of Processing Personal Data in the Platform,
            including:
          </Typography>
          <NumberedList
            items={[
              'which users are invited;',
              'which learning materials are uploaded;',
              'which games, sessions, and examinations are created;',
              'how users participate;',
              'how results, scores, answers, and exports are used;',
              'how long Customer requires Personal Data to be retained, subject to the Platform\u2019s standard functionality and agreed retention terms.',
            ]}
          />
          <Typography variant="body1" paragraph>
            Itclarifies does not determine the purposes for which Customer uses learning results,
            examination outputs, scores, feedback, or exports.
          </Typography>
        </Section>

        <Section title="5. Itclarifies as independent controller">
          <Typography variant="body1" paragraph>
            This DPA does not apply where Itclarifies processes personal data as an independent
            controller for its own business purposes, such as:
          </Typography>
          <NumberedList
            items={[
              'managing commercial contacts;',
              'issuing invoices;',
              'maintaining accounting records;',
              'managing legal claims;',
              'operating its own website;',
              'handling general business communications;',
              'maintaining records required by law;',
              'protecting Itclarifies\u2019 legal rights;',
              'processing limited technical, diagnostic, security, usage, and business-administration data for Itclarifies\u2019 own service operation, security, fraud prevention, product improvement, and legal compliance purposes, where Itclarifies determines the purposes and means of such processing independently.',
            ]}
          />
          <Typography variant="body1" paragraph>
            Such processing is governed by Itclarifies&apos; privacy notice and applicable law, not this
            DPA.
          </Typography>
        </Section>

        <Section title="6. Subject matter, duration, nature, and purpose of Processing">
          <Typography variant="body1" paragraph>
            The subject matter, duration, nature, and purpose of Processing are described in Annex 1.
          </Typography>
          <Typography variant="body1" paragraph>
            The categories of Data Subjects and types of Personal Data are described in Annex 1.
          </Typography>
          <Typography variant="body1" paragraph>
            The technical and organizational security measures are described in Annex 2.
          </Typography>
          <Typography variant="body1" paragraph>
            The authorized Subprocessors are listed in Annex 3.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer&apos;s documented instructions are set out in the Agreement, this DPA, the Order
            Form, Customer&apos;s configuration and settings within the Platform, Customer&apos;s use of the
            Platform&apos;s standard functionality, and any written instructions accepted by Itclarifies.
          </Typography>
        </Section>

        <Section title="7. Customer instructions">
          <Typography variant="body1" paragraph>
            Itclarifies shall Process Personal Data only on Customer&apos;s documented instructions, stated
            below, unless required to do so by EU law, Swedish law, or other applicable law.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer instructs Itclarifies to Process Personal Data as necessary to:
          </Typography>
          <NumberedList
            items={[
              'provide, operate, maintain, secure, monitor, and support the Platform;',
              'create and manage Customer\u2019s database tenant and organization account;',
              'authenticate and authorize users;',
              'host and process uploaded learning materials;',
              'generate games, scenarios, questions, answers, summaries, feedback, and learning content;',
              'run game sessions, learning activities, and examinations;',
              'process answers, results, scores, feedback, and exports;',
              'provide standard support;',
              'detect, prevent, and investigate technical errors, misuse, abuse, and security incidents;',
              'use authorized Subprocessors;',
              'carry out deletion, export, backup, and retention activities;',
              'comply with applicable law.',
            ]}
          />
          <Typography variant="body1" paragraph>
            If Itclarifies believes that an instruction infringes Applicable Data Protection Law,
            Itclarifies shall inform Customer without undue delay, unless prohibited by law.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies is not required to follow an instruction that, in Itclarifies&apos; reasonable
            opinion, would violate applicable law, create a security risk, compromise the Platform,
            infringe third-party rights, or require services outside the scope of the Agreement.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer instructs Itclarifies that, where a user account is removed or deleted through
            the Platform or upon Customer&apos;s written instruction, Itclarifies may de-identify the
            relevant user account by removing Direct Account Identifiers while retaining internal
            technical identifiers and Historical Learning Records where such retention is necessary
            for assessment integrity, learning history, auditability, security, legal compliance,
            dispute resolution.
          </Typography>
        </Section>

        <Section title="8. Customer obligations">
          <Typography variant="body1" paragraph>
            Customer shall:
          </Typography>
          <NumberedList
            items={[
              'comply with Applicable Data Protection Law;',
              'have a valid legal basis for Processing Personal Data in the Platform;',
              'provide all privacy notices to Data Subjects required for Customer\u2019s use of the Platform as Controller, including notices regarding Customer\u2019s purposes, legal basis, use of learning results, scores, answers, feedback, examination data, retention, and internal disclosure of such data;',
              'ensure that Personal Data submitted to the Platform is accurate, relevant, lawful, and limited to what is necessary;',
              'ensure that users are authorized to access the Platform;',
              'manage user roles with defined permissions, and access rights;',
              'remove or disable users who no longer should have access;',
              'ensure that Customer Content does not contain prohibited or excessive Personal Data;',
              'not upload special categories of Personal Data unless expressly agreed in writing;',
              'not upload criminal-conviction data, national identification numbers, health data, children\u2019s data, or highly sensitive HR data unless expressly agreed in writing;',
              'be responsible for responding to Data Subjects where Customer is Controller;',
              'be responsible for determining whether use of learning results, scores, answers, feedback, or examination data complies with employment, education, privacy, and other applicable laws.',
            ]}
          />
          <Typography variant="body1" paragraph>
            Itclarifies may make available Platform-level privacy information, including
            Itclarifies&apos; privacy notice, within the Platform or on its website. Such information
            does not replace Customer&apos;s responsibility to provide notices required for
            Customer&apos;s own Processing purposes as Controller.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer is responsible for:
          </Typography>
          <NumberedList
            items={[
              'determining whether Historical Learning Records relating to a removed or deleted user must be retained for assessment, examination, employment, compliance, audit, legal, or other Customer purposes;',
              'determining how Customer will respond to Data Subject requests, including whether Customer\u2019s response requires explanation, access, export, restriction, de-identification, continued retention, or other action in relation to records Processed in the Platform;',
              'acknowledging that, unless otherwise expressly agreed in writing, Itclarifies\u2019 standard user deletion and account-removal functionality consists of removing or overwriting Direct Account Identifiers, disabling access, removing or disabling active roles and permissions, and retaining internal technical identifiers and Historical Learning Records where permitted under this DPA;',
              'informing Data Subjects, where required, that historical learning, assessment, and session records may be retained in de-identified or pseudonymised form after account removal where necessary for legitimate retention purposes;',
              'ensuring that exports downloaded by Customer are handled, retained, and deleted in accordance with Customer\u2019s own legal obligations and internal policies.',
            ]}
          />
          <Typography variant="body1" paragraph>
            Customer shall not use the Platform in a way that causes Itclarifies to violate Applicable
            Data Protection Law.
          </Typography>
        </Section>

        <Section title="9. Processor obligations">
          <Typography variant="body1" paragraph>
            Itclarifies shall:
          </Typography>
          <NumberedList
            items={[
              'Process Personal Data only on Customer\u2019s documented instructions as set out in this DPA, Annex 4, the Agreement, the Order Form, Customer\u2019s configuration and use of the Platform\u2019s standard functionality, and any additional written instructions accepted by Itclarifies;',
              'ensure that personnel authorized to Process Personal Data are subject to confidentiality obligations;',
              'implement and maintain appropriate technical and organizational measures as described in Annex 2;',
              'assist Customer with Data Subject requests as described in this DPA;',
              'assist Customer with security, breach notification, data-protection impact assessments, and supervisory-authority consultations as described in this DPA;',
              'use only authorized Subprocessors;',
              'impose data-protection obligations on Subprocessors that are substantially equivalent to those in this DPA;',
              'make available information reasonably necessary to demonstrate compliance with this DPA;',
              'delete or return Personal Data at the end of the Services as described in this DPA;',
              'notify Customer if Itclarifies believes an instruction infringes Applicable Data Protection Law.',
            ]}
          />
          <Typography variant="body1" paragraph>
            Itclarifies shall not:
          </Typography>
          <NumberedList
            items={[
              'sell Personal Data;',
              'use Personal Data for another customer\u2019s benefit;',
              'use Personal Data for advertising to Customer\u2019s users;',
              'use Customer Content or Personal Data to train, fine-tune, or improve general-purpose AI models or third-party AI models, unless Customer has given prior written consent;',
              'disclose Personal Data except as permitted by this DPA, the Agreement, Customer\u2019s instructions, or applicable law.',
            ]}
          />
        </Section>

        <Section title="10. Confidentiality">
          <Typography variant="body1" paragraph>
            Itclarifies shall ensure that all personnel who have access to Personal Data are bound by
            confidentiality obligations, whether by employment contract, statutory duty, professional
            obligation, or other binding commitment.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies shall restrict access to Personal Data to personnel who need such access to
            provide, maintain, secure, or support the Platform.
          </Typography>
          <Typography variant="body1" paragraph>
            The confidentiality obligations in this DPA apply in addition to confidentiality
            obligations in the Agreement.
          </Typography>
        </Section>

        <Section title="11. Security measures">
          <Typography variant="body1" paragraph>
            Itclarifies shall implement and maintain appropriate technical and organizational measures
            designed to protect Personal Data against unauthorized or unlawful Processing and against
            accidental or unlawful destruction, loss, alteration, unauthorized disclosure, or access.
            The current security measures are described in Annex 2.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer acknowledges that security measures may evolve over time. Itclarifies may update
            or improve security measures, provided that such changes do not materially reduce the
            overall level of protection for Personal Data.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer is responsible for security measures within Customer&apos;s control, including user
            access, role management, endpoint security, internal policies, user training, and lawful
            use of exports.
          </Typography>
        </Section>

        <Section title="12. Subprocessors">
          <Typography variant="body1" paragraph>
            Customer gives Itclarifies general written authorization to engage Subprocessors for the
            Processing of Personal Data, subject to this section.
          </Typography>
          <Typography variant="body1" paragraph>
            The Subprocessors authorized as of the effective date are listed in Annex 3.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies shall ensure that each Subprocessor is bound by written obligations that
            provide at least the same level of data protection as required by this DPA, to the extent
            applicable to the Subprocessor&apos;s services.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies remains responsible to Customer for the performance of its
            Subprocessors&apos; data-protection obligations in relation to Personal Data Processed on
            behalf of Customer under this DPA.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies may add or replace Subprocessors by giving Customer prior notice, for
            example by email, Platform notification, update to a subprocessor page, or other written
            notice.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer may object to a new Subprocessor only on reasonable data-protection grounds. If
            Itclarifies cannot reasonably accommodate the objection, Customer may terminate the
            affected Services as its sole remedy.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer may not object to a Subprocessor for reasons unrelated to data protection.
          </Typography>
        </Section>

        <Section title="13. International transfers">
          <Typography variant="body1" paragraph>
            Itclarifies shall not transfer Personal Data outside the EU/EEA, including through
            authorized Subprocessors, unless:
          </Typography>
          <NumberedList
            items={[
              'the transfer is made to a country covered by an adequacy decision;',
              'the transfer is subject to the European Commission\u2019s Standard Contractual Clauses;',
              'another valid transfer mechanism under Applicable Data Protection Law applies; or',
              'Customer has expressly instructed or authorized the transfer and the transfer is lawful.',
            ]}
          />
          <Typography variant="body1" paragraph>
            Where required, Itclarifies shall use appropriate safeguards for international transfers,
            including by entering into or relying on applicable data processing terms, Standard
            Contractual Clauses, adequacy decisions, or other lawful transfer mechanisms provided by
            relevant Subprocessors in relation to their services.
          </Typography>
          <Typography variant="body1" paragraph>
            Where the European Commission&apos;s Standard Contractual Clauses are required, the Parties
            agree that the relevant module shall apply based on the roles of the parties involved in
            the transfer.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer authorizes Itclarifies to enter into Standard Contractual Clauses with
            Subprocessors on Customer&apos;s behalf where necessary for providing the Platform.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies shall provide reasonable information available to Itclarifies to Customer
            regarding international transfers upon request.
          </Typography>
        </Section>

        <Section title="14. Data Subject requests">
          <Typography variant="body1" paragraph>
            If Itclarifies receives a request from a Data Subject relating to Personal Data Processed
            on behalf of Customer, Itclarifies shall, unless legally prohibited:
          </Typography>
          <NumberedList
            items={[
              'not respond substantively on Customer\u2019s behalf unless instructed to do so;',
              'inform the Data Subject that the request should be submitted to Customer, where appropriate; and',
              'notify Customer without undue delay, where the request identifies Customer or Customer\u2019s tenant.',
            ]}
          />
          <Typography variant="body1" paragraph>
            Taking into account the nature of the Processing and the information available to
            Itclarifies, Itclarifies shall assist Customer, primarily through the Platform&apos;s
            standard functionality and insofar as reasonably possible, with Customer&apos;s obligations
            to respond to Data Subject requests under Applicable Data Protection Law, including
            requests relating to access, rectification, erasure, restriction of processing, data
            portability, objection, and rights relating to automated decision-making, where
            applicable.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer is responsible for determining whether a Data Subject request is valid, which
            rights apply, whether any exceptions or retention grounds apply, and how Customer will
            respond.
          </Typography>
          <Typography variant="body1" paragraph>
            Where a Data Subject request relates to a user whose Direct Account Identifiers have been
            removed, Itclarifies shall provide reasonable assistance to Customer, taking into account
            the information available to Itclarifies and the technical feasibility of locating
            relevant records.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer acknowledges that, after Direct Account Identifiers have been removed,
            Itclarifies may not be able to identify all records relating to a Data Subject without
            additional information from Customer.
          </Typography>
          <Typography variant="body1" paragraph>
            Where Customer instructs Itclarifies to remove or delete a user account, Itclarifies may
            satisfy such instruction by removing or overwriting Direct Account Identifiers, disabling
            access, removing or disabling active roles and permissions, and retaining internal
            technical identifiers and Historical Learning Records where permitted under this DPA.
          </Typography>
          <Typography variant="body1" paragraph>
            Unless otherwise expressly agreed in writing, Itclarifies&apos; standard user deletion and
            account-removal functionality is limited to removal or overwriting of Direct Account
            Identifiers, disabling access, removing or disabling active roles and permissions, and
            retention of internal technical identifiers and Historical Learning Records where permitted
            under this DPA.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies is not required to perform manual, bespoke, technically disproportionate, or
            security-impacting deletion, reconstruction, or re-identification work unless separately
            agreed in writing.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer is responsible for providing sufficient information to enable Itclarifies to
            identify the relevant Data Subject or records. Itclarifies is not required to search for,
            reconstruct, infer, or recreate identity links that are not reasonably available in the
            Platform.
          </Typography>
          <Typography variant="body1" paragraph>
            If assistance requires significant work outside the Platform&apos;s standard functionality,
            Itclarifies may charge reasonable fees unless the request is caused by Itclarifies&apos;
            breach of this DPA.
          </Typography>
        </Section>

        <Section title="15. Personal Data Breach">
          <Typography variant="body1" paragraph>
            Itclarifies shall notify Customer without undue delay after becoming aware of a Personal
            Data Breach affecting Personal Data Processed on behalf of Customer.
          </Typography>
          <Typography variant="body1" paragraph>
            The notification shall include, to the extent available:
          </Typography>
          <NumberedList
            items={[
              'a description of the nature of the Personal Data Breach;',
              'the categories and approximate number of affected Data Subjects;',
              'the categories and approximate number of affected records;',
              'the likely consequences of the Personal Data Breach;',
              'measures taken or proposed to address the Personal Data Breach;',
              'measures that may mitigate possible adverse effects;',
              'a contact point for further information.',
            ]}
          />
          <Typography variant="body1" paragraph>
            Itclarifies may provide information in phases if not all information is available at the
            same time.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer is responsible for determining whether the Personal Data Breach must be notified
            to a Supervisory Authority or Data Subjects.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies&apos; notification of a Personal Data Breach is not an admission of fault or
            liability.
          </Typography>
        </Section>

        <Section title="16. Assistance with security, DPIAs, and supervisory authorities">
          <Typography variant="body1" paragraph>
            Taking into account the nature of the Processing and information available to Itclarifies,
            Itclarifies shall provide reasonable assistance to Customer with Customer&apos;s obligations
            relating to:
          </Typography>
          <NumberedList
            items={[
              'security of Processing;',
              'Personal Data Breach notifications;',
              'data-protection impact assessments;',
              'prior consultation with Supervisory Authorities.',
            ]}
          />
          <Typography variant="body1" paragraph>
            Such assistance shall be limited to Processing performed by Itclarifies under this DPA.
          </Typography>
          <Typography variant="body1" paragraph>
            If assistance requires significant work outside the Platform&apos;s standard functionality,
            Itclarifies may charge reasonable fees unless the assistance is required due to
            Itclarifies&apos; breach of this DPA.
          </Typography>
        </Section>

        <Section title="17. Records and compliance information">
          <Typography variant="body1" paragraph>
            Itclarifies shall maintain records of Processing activities where required by Applicable
            Data Protection Law.
          </Typography>
          <Typography variant="body1" paragraph>
            Upon Customer&apos;s reasonable written request, Itclarifies shall make available information
            reasonably necessary and reasonably available to demonstrate compliance with this DPA.
          </Typography>
          <Typography variant="body1" paragraph>
            Such information may include subprocessor information and a summary of the technical and
            organizational measures described in Annex 2.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies is not required to create bespoke reports, complete extensive security
            questionnaires, provide certifications it does not hold, disclose information that would
            compromise security, reveal trade secrets, breach confidentiality obligations, expose
            other customers&apos; data, or provide information outside the scope of this DPA.
          </Typography>
        </Section>

        <Section title="18. Audits and inspections">
          <Typography variant="body1" paragraph>
            Customer may audit Itclarifies&apos; compliance with this DPA no more than once per
            calendar year, unless a Personal Data Breach, regulatory investigation, or material breach
            reasonably justifies an additional audit.
          </Typography>
          <Typography variant="body1" paragraph>
            Before conducting an audit, Customer shall first rely on the information, summaries,
            policies, subprocessor information, and security documentation made available by
            Itclarifies. On-site or live audits may only be requested where such documentation is
            insufficient to demonstrate compliance with this DPA and where required by Applicable
            Data Protection Law.
          </Typography>
          <Typography variant="body1" paragraph>
            If that information is insufficient, Customer may request an audit by giving at least 30
            days&apos; written notice.
          </Typography>
          <Typography variant="body1" paragraph>
            Audits shall:
          </Typography>
          <NumberedList
            items={[
              'be limited to Processing under this DPA;',
              'be conducted during normal business hours;',
              'not unreasonably disrupt Itclarifies\u2019 business;',
              'be subject to reasonable security and confidentiality requirements;',
              'not expose other customers\u2019 data;',
              'not require access to source code, trade secrets, or highly sensitive security information;',
              'be conducted by Customer or an independent auditor who is not a competitor of Itclarifies.',
            ]}
          />
          <Typography variant="body1" paragraph>
            Customer shall bear its own audit costs and Itclarifies&apos; reasonable costs of supporting
            the audit, unless the audit reveals a material breach of this DPA by Itclarifies.
          </Typography>
        </Section>

        <Section title="19. Deletion, return, and de-identification of Personal Data">
          <Typography variant="body1" paragraph>
            Upon termination or expiry of the Agreement, Itclarifies shall delete or de-identify
            Personal Data Processed on behalf of Customer in accordance with this DPA, the Agreement,
            Customer&apos;s documented instructions, and Applicable Data Protection Law, unless continued
            retention is required or permitted under this DPA, the Agreement, applicable law, security
            requirements, backup routines, dispute management, audit requirements, or Customer&apos;s
            documented instructions.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer is responsible for exporting any Customer Data or Customer Content that Customer
            wishes to retain before termination or expiry, using the Platform&apos;s standard export
            functionality where available.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies is not required to provide full tenant exports, database exports, custom
            exports, manually prepared exports, or exports of data not available through the
            Platform&apos;s standard functionality unless separately agreed in writing.
          </Typography>
          <Typography variant="body1" paragraph>
            If Itclarifies makes post-termination access or export functionality available, such
            access or export period shall be limited to the period and scope specified in the
            Agreement, Order Form, or written notice from Itclarifies.
          </Typography>
          <Typography variant="body1" paragraph>
            After termination or expiry, and following any agreed access or export period,
            Itclarifies may delete, de-identify, or disable Customer&apos;s tenant, account, Customer
            Data, Customer Content, Personal Data, configuration data, generated content, and related
            records in accordance with the Agreement and this DPA.
          </Typography>
          <Typography variant="body1" paragraph>
            Where Customer instructs Itclarifies to delete or remove a user account, Itclarifies may
            remove, overwrite, or otherwise make unavailable the user&apos;s Direct Account Identifiers,
            including name, username, email address, and external authentication identifiers, while
            retaining internal technical identifiers and Historical Learning Records where necessary
            to:
          </Typography>
          <NumberedList
            items={[
              'preserve referential integrity in the Platform;',
              'maintain assessment, examination, and learning history;',
              'support Customer\u2019s records, exports, reporting, and audit requirements;',
              'comply with legal, regulatory, contractual, accounting, or evidentiary obligations;',
              'maintain security, access logs, and abuse-prevention records;',
              'investigate or resolve disputes;',
              'maintain backup and recovery systems; or',
              'comply with Customer\u2019s documented retention instructions.',
            ]}
          />
          <Typography variant="body1" paragraph>
            Retained internal technical identifiers may include internal database identifiers, session
            identifiers, game identifiers, answer identifiers, audit identifiers, and similar
            identifiers used to maintain the integrity of Platform records.
          </Typography>
          <Typography variant="body1" paragraph>
            Unless the retained data has been anonymised so that the Data Subject is no longer
            identifiable by means reasonably likely to be used, such retained data shall continue to
            be treated as Personal Data under this DPA.
          </Typography>
          <Typography variant="body1" paragraph>
            De-identified User Records and Historical Learning Records shall not be used to re-identify
            a Data Subject except where necessary for Customer&apos;s documented instructions, legal
            compliance, security, dispute resolution, audit purposes, restoration from backup, or other
            purposes permitted under this DPA and Applicable Data Protection Law.
          </Typography>
          <Typography variant="body1" paragraph>
            Personal Data may remain in backups, logs, archives, or recovery systems until overwritten
            or deleted in the ordinary course of retention cycles, provided that such Personal Data
            remains protected and is not actively Processed except for backup, restoration, security,
            continuity, legal, audit, or compliance purposes.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies may retain limited records where required or reasonably necessary to comply
            with legal obligations, resolve disputes, enforce agreements, maintain security, document
            compliance, or establish, exercise, or defend legal claims.
          </Typography>
        </Section>

        <Section title="20. AI processing">
          <Typography variant="body1" paragraph>
            Customer acknowledges that the Platform may use AI or language-model functionality to
            transform Customer Content into learning games, questions, summaries, scenarios, feedback,
            examination materials, or similar outputs.
          </Typography>
          <Typography variant="body1" paragraph>
            Where AI service providers Process Personal Data on behalf of Itclarifies, they shall be
            treated as Subprocessors and listed in Annex 3.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies shall not use Personal Data or Customer Content to train, fine-tune, or
            improve general-purpose AI models or third-party AI models, unless Customer has given
            prior written consent.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer is responsible for ensuring that Personal Data included in uploaded learning
            materials is lawful, necessary, proportionate, and appropriate for AI-assisted processing.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer should avoid uploading unnecessary Personal Data into learning materials.
          </Typography>
        </Section>

        <Section title="21. Sensitive data restrictions">
          <Typography variant="body1" paragraph>
            Unless expressly agreed in writing, Customer shall not upload or otherwise submit to the
            Platform:
          </Typography>
          <NumberedList
            items={[
              'special categories of Personal Data under GDPR Article 9;',
              'health data;',
              'biometric data;',
              'genetic data;',
              'data revealing racial or ethnic origin;',
              'data revealing political opinions;',
              'data revealing religious or philosophical beliefs;',
              'trade-union membership data;',
              'sex-life or sexual-orientation data;',
              'criminal-conviction or offence data;',
              'Swedish personal identity numbers or equivalent national identification numbers;',
              'children\u2019s Personal Data;',
              'patient records;',
              'government-classified, security-classified, export-controlled, or national-security information;',
              'highly sensitive HR investigation files.',
            ]}
          />
          <Typography variant="body1" paragraph>
            If Customer wishes to Process such data, the Parties must first agree in writing on
            additional safeguards, scope, instructions, and commercial terms.
          </Typography>
        </Section>

        <Section title="22. De-identified and aggregated data">
          <Typography variant="body1" paragraph>
            This DPA does not restrict Itclarifies from processing de-identified or aggregated data
            that no longer identifies Customer, users, or Data Subjects.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies may use de-identified or aggregated data for analytics, security, product
            improvement, performance monitoring, and business purposes, provided that such data cannot
            reasonably be used to identify Data Subjects.
          </Typography>
          <Typography variant="body1" paragraph>
            Pseudonymised or de-identified data remains Personal Data and is covered by this DPA
            unless it has been anonymised so that the Data Subject is no longer identifiable by means
            reasonably likely to be used.
          </Typography>
        </Section>

        <Section title="23. Government and authority requests">
          <Typography variant="body1" paragraph>
            If Itclarifies receives a legally binding request from a public authority, court, or
            law-enforcement body for Personal Data Processed on behalf of Customer, Itclarifies shall,
            unless legally prohibited:
          </Typography>
          <NumberedList
            items={[
              'notify Customer without undue delay;',
              'limit disclosure to what is legally required;',
              'reasonably cooperate with Customer if Customer wishes to challenge or limit the request.',
            ]}
          />
          <Typography variant="body1" paragraph>
            Itclarifies is not required to take action that would violate applicable law.
          </Typography>
        </Section>

        <Section title="24. Liability">
          <Typography variant="body1" paragraph>
            Liability under this DPA is governed by the liability provisions in the Agreement,
            unless otherwise required by mandatory Applicable Data Protection Law.
          </Typography>
          <Typography variant="body1" paragraph>
            Nothing in this DPA limits liability that cannot legally be limited.
          </Typography>
        </Section>

        <Section title="25. Term">
          <Typography variant="body1" paragraph>
            This DPA remains in effect for as long as Itclarifies Processes Personal Data on behalf
            of Customer.
          </Typography>
          <Typography variant="body1" paragraph>
            This DPA automatically terminates when Itclarifies has deleted or returned all Personal
            Data in accordance with this DPA, except for provisions that by their nature should
            survive.
          </Typography>
        </Section>

        <Section title="26. Order of precedence">
          <Typography variant="body1" paragraph>
            If there is a conflict between this DPA and the Agreement regarding Processing of
            Personal Data, this DPA prevails. For clarity, this DPA governs Processing of Personal
            Data and related data-protection obligations only. Nothing in this DPA transfers ownership
            of Customer Content, Customer-Specific Generated Content, Platform IP, or any other
            intellectual-property rights, which are governed by the Agreement.
          </Typography>
          <Typography variant="body1" paragraph>
            If there is a conflict between this DPA and Standard Contractual Clauses that apply to
            an international transfer, the Standard Contractual Clauses prevail for that transfer.
          </Typography>
        </Section>

        <Section title="27. Governing law and disputes">
          <Typography variant="body1" paragraph>
            This DPA is governed by Swedish law, without regard to conflict-of-law rules.
          </Typography>
          <Typography variant="body1" paragraph>
            Disputes shall be resolved in accordance with the dispute-resolution clause in the
            Agreement.
          </Typography>
          <Typography variant="body1" paragraph>
            Unless otherwise agreed, the Swedish courts shall have jurisdiction, with Stockholm
            District Court as the court of first instance.
          </Typography>
        </Section>

        <Section title="28. Acceptance and incorporation">
          <Typography variant="body1" paragraph>
            This DPA forms part of the Agreement between Itclarifies and Customer.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer accepts this DPA when Customer accepts the Agreement, starts a subscription,
            pays an invoice for the Services, requests or authorizes creation or activation of a
            tenant, accesses or uses the Platform, or otherwise accepts the Agreement by written,
            electronic, or practical means.
          </Typography>
          <Typography variant="body1" paragraph>
            No separate signature of this DPA is required.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
          Annexes
        </Typography>
        <BulletList
          items={[
            'Annex 1 – Details of Processing',
            'Annex 2 – Technical and Organizational Measures',
            'Annex 3 – Authorized Subprocessors',
            'Annex 4 – Customer\u2019s Documented Instructions',
            'Annex 5 – Security Contact and Breach Notification Contact',
            'Annex 6 – Optional EU Standard Contractual Clauses Addendum',
          ]}
        />

        <Section title="Annex 1 – Details of Processing">
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            1. Subject matter of Processing
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies Processes Personal Data to provide the Itclarifies learning platform to
            Customer, including tenant management, user management, authentication, learning-content
            processing, content transformation, game creation, game sessions, examination flows, session
            results, feedback, exports, support, security, maintenance, and troubleshooting.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            2. Duration of Processing
          </Typography>
          <Typography variant="body1" paragraph>
            Personal Data is Processed for the duration of the Agreement.
          </Typography>
          <Typography variant="body1" paragraph>
            After termination or expiry, Personal Data is Processed only as necessary for:
          </Typography>
          <NumberedList
            items={[
              'export;',
              'deletion;',
              'backup retention;',
              'legal compliance;',
              'dispute management;',
              'security;',
              'audit logs;',
              'ordinary system-retention cycles.',
            ]}
          />

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            3. Nature of Processing
          </Typography>
          <Typography variant="body1" paragraph>
            The Processing may include:
          </Typography>
          <NumberedList
            items={[
              'collection;',
              'recording;',
              'organization;',
              'structuring;',
              'storage;',
              'hosting;',
              'retrieval;',
              'consultation;',
              'use;',
              'transmission;',
              'analysis;',
              'transformation;',
              'generation of learning content;',
              'scoring;',
              'reporting;',
              'export;',
              'deletion;',
              'de-identification;',
              'backup;',
              'troubleshooting;',
              'security monitoring.',
            ]}
          />

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            4. Purpose of Processing
          </Typography>
          <Typography variant="body1" paragraph>
            The purpose is to provide the Platform and related Services, including:
          </Typography>
          <NumberedList
            items={[
              'creating and managing Customer\u2019s tenant;',
              'enabling Customer administrators to manage users;',
              'enabling creators to upload learning content;',
              'transforming learning content into games, scenarios, questions, feedback, and examination materials;',
              'enabling users to participate in learning sessions;',
              'storing and displaying session results;',
              'enabling exports for examination and training purposes;',
              'providing support;',
              'maintaining security, integrity, availability, and reliability;',
              'complying with applicable law.',
            ]}
          />
          <Typography variant="body1" paragraph>
            The purpose of Processing also includes de-identifying user accounts by removing Direct
            Account Identifiers while retaining internal technical identifiers and Historical Learning
            Records where necessary for assessment integrity, auditability, referential integrity,
            Customer reporting, legal compliance, security, dispute resolution, backup management, or
            Customer&apos;s documented retention instructions.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            5. Categories of Data Subjects
          </Typography>
          <Typography variant="body1" paragraph>
            The Data Subjects may include:
          </Typography>
          <NumberedList
            items={[
              'Customer\u2019s employees;',
              'Customer\u2019s consultants;',
              'Customer\u2019s contractors;',
              'Customer\u2019s trainees;',
              'Customer\u2019s administrators;',
              'Customer\u2019s creators;',
              'Customer\u2019s hosts;',
              'Customer\u2019s game masters;',
              'Customer\u2019s players or participants;',
              'Customer\u2019s business contacts;',
              'individuals mentioned incidentally in Customer Content uploaded by Customer.',
            ]}
          />

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            6. Categories of Personal Data
          </Typography>
          <Typography variant="body1" paragraph>
            The Personal Data may include:
          </Typography>
          <NumberedList
            items={[
              'name;',
              'email address;',
              'organization affiliation;',
              'user role;',
              'account ID;',
              'authentication identifier;',
              'tenant or organization identifier;',
              'game-space or group membership;',
              'login and access metadata;',
              'IP address;',
              'device and browser metadata;',
              'audit logs;',
              'uploaded learning-content metadata;',
              'game participation data;',
              'session ID;',
              'game ID;',
              'answers submitted by users;',
              'scores;',
              'progress data;',
              'completion status;',
              'generated feedback;',
              'examination results;',
              'exported reports;',
              'support communications;',
              'personal data included by Customer in uploaded learning content.',
            ]}
          />
          <Typography variant="body1" paragraph>
            The Personal Data may also include retained internal technical identifiers and
            pseudonymised historical records after removal of Direct Account Identifiers, including:
          </Typography>
          <NumberedList
            items={[
              'internal user ID;',
              'session ID;',
              'game ID;',
              'answer ID;',
              'score records;',
              'completion records;',
              'assessment records;',
              'audit metadata;',
              'export metadata;',
              'deletion or de-identification metadata.',
            ]}
          />

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            7. Special categories of Personal Data
          </Typography>
          <Typography variant="body1" paragraph>
            The Platform is not intended for special categories of Personal Data.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer shall not upload special categories of Personal Data unless expressly agreed in
            writing.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            8. Frequency of Processing
          </Typography>
          <Typography variant="body1" paragraph>
            Processing occurs continuously during Customer&apos;s use of the Platform.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            9. Location of Processing
          </Typography>
          <Typography variant="body1" paragraph>
            Primary hosting location: Microsoft Azure West Europe, EU/EEA.
          </Typography>
          <Typography variant="body1" paragraph>
            Support and subprocessor locations: as described in Annex 3.
          </Typography>
          <Typography variant="body1" paragraph>
            International transfers: only as permitted under Section 13.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            10. Controller obligations and rights
          </Typography>
          <Typography variant="body1" paragraph>
            Customer has the rights and obligations described in the Agreement, this DPA, and
            Applicable Data Protection Law.
          </Typography>
        </Section>

        <Section title="Annex 2 – Technical and Organizational Measures">
          <Typography variant="body1" paragraph>
            This Annex describes the current technical and organizational measures applied by
            Itclarifies to protect Personal Data Processed on behalf of Customer.
          </Typography>
          <Typography variant="body1" paragraph>
            The measures are intended to be appropriate to the nature, scope, context, and risk of the
            Processing, taking into account the Platform&apos;s current functionality, Itclarifies&apos;
            size and resources, the type of Personal Data permitted under the Agreement and this DPA,
            and Itclarifies&apos; use of reputable cloud, identity, AI, and infrastructure providers.
          </Typography>
          <Typography variant="body1" paragraph>
            The Platform is not designed or offered as critical infrastructure, a high-security
            system, a regulated examination system, or a system intended for special categories of
            Personal Data, criminal-conviction data, health data, children&apos;s data, national
            identification numbers, classified information, or highly sensitive HR data, unless
            expressly agreed in writing.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies does not represent that it maintains ISO 27001, SOC 2, or equivalent
            certification, a formal enterprise security-management system, a dedicated security
            operations center, independent penetration testing, formal disaster-recovery testing, or
            enterprise-grade key-management infrastructure, unless expressly stated in an Order Form.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            1. Governance and responsibility
          </Typography>
          <Typography variant="body1" paragraph>
            Responsibility for privacy, security, infrastructure configuration, supplier selection,
            and Platform development is handled directly by Itclarifies&apos; management.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies applies reasonable operational practices intended to protect Customer Data,
            including limiting administrative access, using reputable service providers, separating
            development and production environments where applicable, restricting the types of
            Personal Data Customers may upload, and reviewing relevant security and privacy measures
            as the Platform evolves.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            2. Access control
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies uses access-control measures designed to limit access to Personal Data to
            authorized persons.
          </Typography>
          <Typography variant="body1" paragraph>
            Measures may include role-based access controls in the Platform, individual administrator
            access where supported by relevant systems, authentication and authorization through
            third-party identity providers, limited production access, and removal or disabling of
            access when no longer needed.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer is responsible for managing its own users, invitations, roles, permissions, and
            account removal within the Platform.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            3. Tenant and data separation
          </Typography>
          <Typography variant="body1" paragraph>
            The Platform is designed to support logical separation of Customer Data between customers,
            organizations, tenants, game spaces, and user roles.
          </Typography>
          <Typography variant="body1" paragraph>
            Measures may include tenant-specific configuration, organization-level scoping, game-space-
            level scoping, role-based permissions, application-level access checks, database
            relationships and access logic, and use of public-facing identifiers in external
            interfaces where applicable.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer acknowledges that tenant separation is logical separation and does not require
            physically separate infrastructure unless expressly agreed in an Order Form.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            4. Transmission security, encryption, and credentials
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies uses reasonable measures to protect Personal Data during transmission and to
            restrict access to operational credentials.
          </Typography>
          <Typography variant="body1" paragraph>
            Measures may include HTTPS/TLS for browser and API traffic, secure connections to
            cloud-hosted services where supported, restricted access to production configuration,
            storage of operational credentials in environment variables or provider-supported
            configuration mechanisms, avoiding inclusion of secrets in public repositories, and
            rotating or replacing credentials where Itclarifies reasonably believes credentials may
            have been exposed or compromised.
          </Typography>
          <Typography variant="body1" paragraph>
            Encryption at rest may be provided by Itclarifies&apos; hosting, database, storage, or
            infrastructure providers. Itclarifies does not separately warrant customer-managed
            encryption keys, dedicated encryption infrastructure, or bespoke encryption arrangements
            unless expressly agreed in an Order Form.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            5. Logging and monitoring
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies uses logging and monitoring to the extent reasonably available through the
            Platform, cloud providers, authentication providers, application framework, or other
            infrastructure providers.
          </Typography>
          <Typography variant="body1" paragraph>
            Measures may include application logs, error logs, authentication or access logs provided
            by identity providers, infrastructure logs provided by hosting providers, and review of
            logs when investigating errors, support issues, suspected misuse, or security incidents.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies does not represent that it provides continuous manual monitoring, a dedicated
            security operations center, real-time threat hunting, formal log-review procedures, or
            guaranteed alerting unless expressly agreed in an Order Form.
          </Typography>
          <Typography variant="body1" paragraph>
            Logs may contain limited Personal Data, such as account identifiers, IP addresses,
            timestamps, request metadata, technical error data, or support-related information. Logs
            are used for operational, troubleshooting, security, legal, and compliance purposes.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            6. Availability, backup, and recovery
          </Typography>
          <Typography variant="body1" paragraph>
            The Platform relies on cloud-hosted infrastructure and third-party providers for
            availability, hosting, storage, database functionality, authentication, and related
            operational services.
          </Typography>
          <Typography variant="body1" paragraph>
            Measures may include use of reputable cloud infrastructure providers, backup, redundancy,
            retention, and recovery features provided by relevant infrastructure providers,
            separation between development and production environments, ability to redeploy application
            components from source code and configuration, and commercially reasonable efforts to
            restore service following incidents.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer acknowledges that the Platform is not offered as critical infrastructure and that
            Itclarifies does not provide a formal disaster-recovery plan, guaranteed recovery time
            objective, guaranteed recovery point objective, high-availability commitment, or uptime
            SLA unless expressly agreed in an Order Form.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            7. Development and deployment
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies develops and maintains the Platform using development practices appropriate to
            an early-stage SaaS provider.
          </Typography>
          <Typography variant="body1" paragraph>
            Measures may include use of version control, separation between development and production
            environments, review of material changes by the developer responsible for the Platform,
            testing of changes where reasonably practicable before deployment, correction of identified
            bugs and security issues based on severity and available resources, and deployment
            through controlled development workflows where applicable.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies does not represent that it maintains formal secure software development
            lifecycle certification, independent code review, mandatory penetration testing, formal
            release approval boards, automated security testing, or formal test procedures unless
            expressly agreed in an Order Form.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            8. Subprocessors and infrastructure providers
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies relies on third-party providers for certain parts of the Platform, including
            hosting, infrastructure, authentication, AI processing, email, logging, monitoring,
            support, and related services.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies uses commercially reasonable efforts to select reputable providers and to
            ensure that providers Processing Personal Data are subject to appropriate contractual
            data-protection obligations.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer acknowledges that certain security, availability, backup, logging, encryption,
            physical security, and access-control measures are provided by such third-party providers
            and are subject to their respective technical and organizational measures.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            9. Confidentiality and personnel
          </Typography>
          <Typography variant="body1" paragraph>
            Persons authorized by Itclarifies to access Personal Data are subject to confidentiality
            obligations, whether by contract, legal obligation, or role-based responsibility.
          </Typography>
          <Typography variant="body1" paragraph>
            Access to Personal Data is limited to persons who require such access to operate,
            maintain, secure, troubleshoot, or support the Platform.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            10. Data minimization and sensitive data restrictions
          </Typography>
          <Typography variant="body1" paragraph>
            The Platform is not intended for special categories of Personal Data, criminal-conviction
            data, health data, children&apos;s data, national identification numbers, classified
            information, or highly sensitive HR data unless expressly agreed in writing.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer is responsible for ensuring that uploaded learning materials, prompts, answers,
            free-text fields, exports, and other Customer-controlled content do not contain
            unnecessary or prohibited Personal Data.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies may process Personal Data contained in Customer Content as submitted by
            Customer but is not responsible for classifying, reviewing, or removing Personal Data from
            Customer Content unless expressly agreed in writing.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            11. AI processing safeguards
          </Typography>
          <Typography variant="body1" paragraph>
            Where AI services are used, Itclarifies uses reasonable measures designed to limit
            unnecessary exposure of Personal Data.
          </Typography>
          <Typography variant="body1" paragraph>
            Measures may include processing Customer Content only to provide Customer-specific outputs,
            not using Customer Content or Personal Data to train general-purpose AI models unless
            Customer has given prior written consent, limiting prompts and outputs to what is
            necessary for the requested function, and requiring AI providers that Process Personal Data
            to act as Subprocessors or equivalent service providers.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer is responsible for avoiding unnecessary Personal Data in materials, prompts,
            free-text answers, and other content submitted for AI-assisted processing.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            12. De-identification and account removal
          </Typography>
          <Typography variant="body1" paragraph>
            Where Customer removes or deletes a user account through the Platform or instructs
            Itclarifies to do so, Itclarifies may remove or overwrite Direct Account Identifiers
            while retaining internal technical identifiers and Historical Learning Records where
            permitted under the DPA.
          </Typography>
          <Typography variant="body1" paragraph>
            Measures may include removal or overwriting of name, username, email address, and external
            authentication identifiers; setting user records to a deleted, removed, disabled, or
            equivalent status; preventing removed users from accessing the Platform; removing or
            disabling active roles and permissions; retaining internal identifiers where necessary for
            referential integrity; displaying removed users using neutral labels such as &ldquo;Deleted
            user&rdquo;; and preserving historical session, answer, score, assessment, audit, and
            export records where necessary for Customer&apos;s records, legal compliance, security, or
            auditability.
          </Typography>
          <Typography variant="body1" paragraph>
            Retained pseudonymised records remain protected under this DPA unless they have been de-
            identified so that the Data Subject is no longer identifiable by means reasonably likely
            to be used.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            13. Incident handling
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies will use commercially reasonable efforts to investigate suspected security
            incidents affecting Personal Data Processed on behalf of Customer.
          </Typography>
          <Typography variant="body1" paragraph>
            Where Itclarifies becomes aware of a Personal Data Breach affecting Personal Data
            Processed on behalf of Customer, Itclarifies will notify Customer without undue delay in
            accordance with the DPA.
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies does not represent that it maintains a dedicated incident-response team, 24/7
            incident hotline, formal incident-response certification, or continuous manual incident
            monitoring unless expressly agreed in an Order Form.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            14. Customer-controlled security responsibilities
          </Typography>
          <Typography variant="body1" paragraph>
            Customer is responsible for security and compliance measures within Customer&apos;s
            control, including selecting appropriate users, managing invitations, roles, permissions,
            and account removal, ensuring that users protect their credentials, using secure devices,
            browsers, and networks, limiting uploaded Personal Data to what is necessary, avoiding
            prohibited or sensitive Personal Data unless expressly agreed, managing exported reports
            and downloaded files, determining retention periods and lawful bases for Processing, and
            reviewing generated content and learning results before relying on them.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer acknowledges that exports downloaded from the Platform are under Customer&apos;s
            control and are no longer protected by Itclarifies&apos; Platform security measures.
          </Typography>
        </Section>

        <Section title="Annex 3 – Authorized Subprocessors">
          <Typography variant="body1" paragraph>
            This Annex has been completed where possible based on the current technical architecture
            overview. Legal entity names, provider-specific processing locations, configuration
            details, and transfer mechanisms should be verified against the applicable provider data
            processing terms before signature.
          </Typography>
          <Typography variant="body1" paragraph>
            Customer authorizes Itclarifies to use the following Subprocessors:
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, mt: 2 }}>
            1. Platform Subprocessors
          </Typography>

          <SubprocessorEntry
            name="Microsoft Azure / Microsoft Ireland Operations Limited"
            purpose="Cloud hosting, database, storage, infrastructure"
            categories="Customer Data, user data, session data, uploaded content, logs"
            location="Microsoft Azure West Europe, EU/EEA"
            transfer="Primary hosting in EU/EEA."
          />

          <SubprocessorEntry
            name="Auth0 by Okta Inc. (USA)"
            purpose="Authentication, identity management, login, authorization, role handling, organization separation, tenant-related access control, and related identity-security functionality."
            categories="Name, email address, authentication identifier, Auth0 user ID, organization affiliation, role/account data, login metadata, IP address, device/browser metadata, access logs, and other identity-related metadata."
            location="EU-2 region for the Auth0 tenant, subject to Auth0/Okta&apos;s applicable data residency and service terms."
            transfer="EU-2 processing location; Auth0/Okta DPA; SCCs/adequacy or other lawful transfer mechanism for any non-EEA transfer."
          />

          <SubprocessorEntry
            name="OpenAI / [legal entity]"
            purpose="AI-assisted processing to support learning-content transformation, game creation, scenario generation, question generation, answer-option generation, learning feedback, facilitation support, summarisation, post-processing, and related AI-assisted Platform functionality. OpenAI is not used as the primary hosting environment for the Platform."
            categories="Uploaded learning content submitted for AI processing; prompts and instructions; generated outputs; summaries; scenarios; questions; answer options; feedback; limited user/session/game context where needed for AI-assisted functionality; and possible incidental Personal Data included by Customer or users in uploaded materials, prompts, answers, or free-text content. No special categories of Personal Data are intended to be transferred unless unexpectedly included by Customer or users in unstructured content."
            location="Global / OpenAI infrastructure and subprocessors used to provide the API services. Itclarifies does not currently configure or warrant EU-only processing for OpenAI API processing."
            transfer="OpenAI Data Processing Addendum and applicable OpenAI service terms. For EEA/Swiss Personal Data transferred outside the EU/EEA or Switzerland, OpenAI&apos;s DPA provides for SCCs, adequacy decisions, or other lawful transfer mechanisms, as applicable."
          />

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, mt: 2 }}>
            2. Customer/Support communication, Payment, invoicing, and accounting providers
          </Typography>

          <SubprocessorEntry
            name="Google Workspace / relevant Google contracting entity under Itclarifies&apos; Google Workspace agreement"
            purpose="Business email, calendar, document collaboration, internal workspace services, customer communications, support correspondence, storage of business documents, and administration of customer-related communications. Google Workspace is not used as the primary hosting environment for the Platform."
            categories="Business contact details, names, email addresses, email metadata, support correspondence, meeting/calendar information, customer communications, contractual documents, DPA/order-form documents, attachments or files voluntarily sent to Itclarifies, and limited Platform/user information included in support or business communications."
            location="Global / Google data centers used for Google Workspace, as described in Google&apos;s published data-center and service documentation."
            transfer="Google Cloud Data Processing Addendum for Google Workspace, including applicable SCCs, adequacy decisions, or other lawful transfer mechanisms described in Google&apos;s terms."
          />

          <SubprocessorEntry
            name="Fortnox AB"
            purpose="Invoice generation, accounting administration, bookkeeping support, customer register management, VAT/tax-related accounting records, payment reconciliation, and administration of organization customer invoices. Fortnox is not used as the primary hosting environment for the Platform."
            categories="Business contact details, customer billing details, organization name, contact person name, email address, billing address, invoice reference, VAT/tax information, invoice data, payment status, accounting records, and limited customer/account identifiers needed for invoicing and bookkeeping administration."
            location="Primarily Sweden/EU/EEA, subject to Fortnox&apos;s applicable terms, subprocessors, and service documentation."
            transfer="Fortnox data processing terms/personuppgiftsbiträdesavtal and applicable Fortnox privacy/data-protection terms. If Personal Data is transferred outside the EU/EEA, applicable SCCs, adequacy decisions, or other lawful transfer mechanisms apply under Fortnox&apos;s terms."
          />

          <SubprocessorEntry
            name="Stripe / relevant Stripe contracting entity under Itclarifies&apos; Stripe agreement"
            purpose="Payment processing, subscription management, invoicing support, payment administration, tax/VAT-related payment records, fraud prevention, and payment-related customer communications. Stripe is not used as the primary hosting environment for the Platform."
            categories="Business contact details, customer billing details, name, email address, company name, billing address, VAT/tax information, payment method metadata, transaction details, subscription status, invoice data, payment status, fraud-prevention signals, and limited Platform/customer account identifiers needed for billing administration. Itclarifies should not store full card numbers in the Platform."
            location="Global / Stripe infrastructure and subprocessors used to provide payment services, as described in Stripe&apos;s published legal and privacy documentation."
            transfer="Stripe data processing terms or equivalent Stripe privacy/data-protection terms applicable to Itclarifies&apos; Stripe account, including applicable SCCs, adequacy decisions, or other lawful transfer mechanisms where required."
          />

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, mt: 2 }}>
            Subprocessor-change notice
          </Typography>
          <Typography variant="body1" paragraph>
            The Subprocessors listed in this Annex are authorized as of the effective date of this DPA.
            Itclarifies shall notify Customer of new or replacement Subprocessors in accordance with
            Section 12 of the DPA.
          </Typography>
        </Section>

        <Section title="Annex 4 – Customer&apos;s Documented Instructions">
          <Typography variant="body1" paragraph>
            Customer instructs Itclarifies to Process Personal Data as follows:
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            1. Platform operation
          </Typography>
          <Typography variant="body1" paragraph>
            Process Personal Data to provide, operate, maintain, monitor, improve, troubleshoot, and
            secure the Platform.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            2. User management
          </Typography>
          <Typography variant="body1" paragraph>
            Process Personal Data to create, authenticate, authorize, manage, and support users.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            3. Tenant and organization management
          </Typography>
          <Typography variant="body1" paragraph>
            Process Personal Data to configure and operate Customer&apos;s tenant, organization account,
            game spaces, roles, permissions, and related settings.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            4. Learning-content processing
          </Typography>
          <Typography variant="body1" paragraph>
            Process Customer-uploaded learning content to create, generate, transform, store, display,
            and export learning games, scenarios, questions, answers, summaries, explanations, and
            feedback.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            5. Session and result processing
          </Typography>
          <Typography variant="body1" paragraph>
            Process Personal Data to operate game sessions, store participation records, process
            answers, calculate results, provide feedback, generate exports, and support
            examination-related use.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            6. Support
          </Typography>
          <Typography variant="body1" paragraph>
            Process Personal Data to provide technical and customer support.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            7. Security
          </Typography>
          <Typography variant="body1" paragraph>
            Process Personal Data to detect, prevent, investigate, and respond to abuse, misuse,
            errors, unauthorized access, and security incidents.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            8. Subprocessors
          </Typography>
          <Typography variant="body1" paragraph>
            Use the authorized Subprocessors listed in Annex 3 and new Subprocessors added in
            accordance with the DPA.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            9. Transfers
          </Typography>
          <Typography variant="body1" paragraph>
            Transfer Personal Data as permitted under Section 13 of the DPA.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            10. Deletion and export
          </Typography>
          <Typography variant="body1" paragraph>
            Return, export, delete, de-identify, retain, or backup Personal Data in accordance with
            the Agreement, this DPA, Customer&apos;s instructions, and applicable law.
          </Typography>
        </Section>

        <Section title="Annex 5 – Security Contact and Breach Notification Contact">
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            Customer contact
          </Typography>
          <Typography variant="body1" paragraph>
            Security/privacy contact name: Organization Admin as defined in tenant on Platform.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            Itclarifies contact
          </Typography>
          <Typography variant="body1" paragraph>
            Security/privacy contact name: Ulf Änggård
            <br />
            Email:{' '}
            <Link href="mailto:info@itclarifies.com">info@itclarifies.com</Link>
          </Typography>

          <Typography variant="body1" paragraph>
            Customer is responsible for keeping its security and privacy contact information up to
            date.
          </Typography>
        </Section>

        <Section title="Annex 6 – Optional EU Standard Contractual Clauses Addendum">
          <Typography variant="body1" paragraph>
            This Annex applies only where Personal Data is transferred outside the EU/EEA and the
            transfer requires Standard Contractual Clauses.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            1. Incorporation
          </Typography>
          <Typography variant="body1" paragraph>
            Where required by Applicable Data Protection Law, the applicable European Commission
            Standard Contractual Clauses are incorporated into this DPA by reference.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            2. Module selection
          </Typography>
          <Typography variant="body1" paragraph>
            The applicable module shall be determined by the roles of the parties in the relevant
            transfer:
          </Typography>
          <NumberedList
            items={[
              'Controller-to-Processor Module where Customer transfers Personal Data to Itclarifies outside the EU/EEA.',
              'Processor-to-Processor Module where Itclarifies transfers Personal Data to a Subprocessor outside the EU/EEA.',
              'Other modules only where applicable to the relevant transfer.',
            ]}
          />

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            3. Conflict
          </Typography>
          <Typography variant="body1" paragraph>
            If there is a conflict between this DPA and the Standard Contractual Clauses, the Standard
            Contractual Clauses prevail for the relevant transfer.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            4. Transfer impact
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies shall provide reasonable information available to it to assist Customer with
            transfer-risk assessments where required.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            5. Subprocessor transfers
          </Typography>
          <Typography variant="body1" paragraph>
            Customer authorizes Itclarifies to enter into Standard Contractual Clauses with
            Subprocessors where necessary for providing the Services.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
          Version History
        </Typography>
        <Table sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Version</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Summary of Changes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>1.0</TableCell>
              <TableCell>2026-06-15</TableCell>
              <TableCell>Initial release of Data Processing Agreement for the Itclarifies learning platform.</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Container>

      <HomeFooter />
    </>
  );
};

export default DPA;
